import { useState, useRef, useEffect, ChangeEvent, FormEvent } from "react";
import { ConversationNavbar } from "@/ui/components/navigation";
import {
  ConversationMain,
  ConversationBottomNav,
  ConversationMenuModal,
  ConversationLoadingModal,
} from "@/ui/components/chat";
import { MessageUI, QuickActionUI } from "@/ui/components/chat/types";
import { useChatService } from "@/application/services/chatService";
import { usePageService } from "@/application/services/pageService";

/**
 * 對話式建站頁面
 * 顯示與 AI 助手的對話介面
 */
export function ConversationPage() {
  const { getPageMessages, sendMessage } = useChatService;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [messages, setMessages] = useState<MessageUI[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

  // 快速操作選項
  const [quickActions, setQuickActions] = useState<QuickActionUI[]>([]);

  useEffect(() => {
    const getUserContext = async () => {
      const context = await usePageService.getUserContext();
      if (!context.current_project_id || !context.current_page_id) return;
      const page = await usePageService.getPageById(context.current_project_id, context.current_page_id);
      setSessionId(page.page_conversation_id);
    };

    getUserContext();
  }, []);

  const getMessages = async () => {
    if (!sessionId) return;
    const messages = await getPageMessages(sessionId);
    if (messages.length === 0) return;
    const lastMessage = messages[messages.length - 1];
    const formatContent = (message: string): [content: string, meta: string] => {
      const splits = message.split('\nmeta: ');
      return [splits[0].trim(), splits[1]?.trim()];
    }
    if (lastMessage.role !== "user") {
      try {
        const [_, meta] = formatContent(lastMessage.content);
        const metaJson = meta ? JSON.parse(meta) : {};
        const options = metaJson.options || [];
        setQuickActions(options.map((option: string) => ({
          id: option,
          text: option,
          icon: "",
        }))); 
      } catch (error) {
        console.error(error);
        setQuickActions([]);
      }
      setIsLoading(false);
      if (timer) clearTimeout(timer);
    } else {
      // createdAt is ISO 8601 format so need to add timezone offset
      const diff = (new Date()).getTime() - new Date(lastMessage.created_at).getTime() + (new Date()).getTimezoneOffset() * 60 * 1000;
      if (diff < 10 * 60 * 1000) {
        setIsLoading(true);
      } else {
        setIsLoading(false);
        if (timer) clearTimeout(timer);
      }
    }
    setMessages(messages.map((message) => ({
      id: message.id,
      content: formatContent(message.content)[0],
      sender: message.role === "user" ? "user" : "ai",
      timestamp: new Date(message.created_at),
    })));
  };

  const setAutoGetMessages = () => {
    const timer = setTimeout(() => {
      getMessages();
    }, 3 * 1000);
    setTimer(timer);
  };

  useEffect(() => {
    if (isLoading) {
      setAutoGetMessages();
    }
  }, [isLoading]);

  useEffect(() => {
    if (!sessionId) return;
    getMessages();
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // 自動調整文字輸入框高度
  const autoResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        128,
      )}px`;
    }
  };

  // 處理輸入變更
  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.currentTarget.value);
    autoResize();
  };

  // 處理按鍵事件
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e as unknown as FormEvent);
    }
  };

  // 處理訊息發送
  const handleSendMessage = async (e?: FormEvent, messageText?: string) => {
    if (e) e.preventDefault();
    const textToSend = messageText !== undefined ? messageText : inputValue;
    if (!textToSend.trim() || isLoading) return;
    if (!sessionId) return;

    // 添加用戶訊息
    const userMessage: MessageUI = {
      id: Date.now().toString(),
      content: textToSend.trim(),
      sender: "user",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);
    
    // 自動調整文字輸入框高度
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
    await sendMessage(sessionId, textToSend);
    setAutoGetMessages();
  };

  // 處理快速操作點擊
  const handleQuickActionClick = (action: QuickActionUI) => {
    setInputValue(action.text);
    if (textareaRef.current) {
      textareaRef.current.focus();
      autoResize();
    }
    handleSendMessage(undefined, action.text);
  };

  // 切換選單
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      <ConversationNavbar onMenuClick={toggleMenu} />
      <ConversationMain messages={messages} messagesEndRef={messagesEndRef} />
      <ConversationBottomNav
        quickActions={quickActions}
        textareaRef={textareaRef}
        isLoading={isLoading}
        inputValue={inputValue}
        handleInputChange={handleInputChange}
        handleKeyPress={handleKeyPress}
        handleSendMessage={handleSendMessage}
        handleQuickActionClick={handleQuickActionClick}
      />
      <ConversationMenuModal
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={toggleMenu}
      />
      <ConversationLoadingModal isLoading={isLoading} />
    </>
  );
}
